import * as React from 'react';
import { cn } from '../lib/utils';

export type TransmissionDistributionChartProps = {
  /**
   * Array of 4 numeric values representing material counts for each range:
   * [0] = 80-100% range
   * [1] = 60-79% range
   * [2] = 30-59% range
   * [3] = 0-29% range
   */
  values: [number, number, number, number];
  /** Additional CSS class for the container */
  className?: string;
};

interface RangeConfig {
  label: string;
  color: string;
}

const RANGE_CONFIGS: RangeConfig[] = [
  { label: '80-100%', color: 'var(--color-success)' },
  { label: '60-79%', color: 'var(--color-warning)' },
  { label: '30-59%', color: 'var(--color-error)' },
  { label: '0-29%', color: 'var(--color-error-dark)' },
];

/**
 * Transmission Distribution Chart component following Shelflix Admin design system.
 *
 * Displays 4 rows showing material distribution across transmission percentage ranges.
 * Each row shows:
 * - Range label (e.g., "80-100%")
 * - Material count (e.g., "2050 materiais")
 * - Percentage of total
 * - Progress bar
 *
 * Specifications:
 * - Range label: 18px, weight 400, --color-neutral-200
 * - Material count: 14px, weight 400, --color-neutral-200
 * - Percentage: colored per range (success, warning, error, error-dark)
 * - Progress bars: colored per range, transparent background
 * - Gap: 16px (gap-4) between all rows
 */
const TransmissionDistributionChart = React.forwardRef<HTMLDivElement, TransmissionDistributionChartProps>(
  ({ values, className }, ref) => {
    // Calculate total for percentage computation
    const total = values.reduce((sum, val) => sum + val, 0);

    return (
      <div ref={ref} className={cn('flex w-full flex-col gap-6', className)}>
        {RANGE_CONFIGS.map((config, index) => {
          const value = values[index];
          const percentage = total > 0 ? (value / total) * 100 : 0;

          return (
            <div key={config.label} className="flex flex-col gap-6">
              {/* Header row: range label, material count, percentage */}
              <div className="flex items-center justify-between">
                {/* Left side: range label */}
                <span className="text-[18px] font-normal text-[var(--color-neutral-200)]">{config.label}</span>

                {/* Right side: material count + percentage */}
                <div className="flex items-center gap-3">
                  <span className="text-[14px] font-normal text-[var(--color-neutral-200)]">
                    {value.toLocaleString('pt-BR')} materiais
                  </span>
                  <span className="text-[14px] font-normal" style={{ color: config.color }}>
                    {percentage.toFixed(1).replace('.', ',')}%
                  </span>
                </div>
              </div>

              {/* Progress bar */}
              <div className="relative h-4 w-full overflow-hidden rounded-[50px] bg-transparent">
                {/* Progress fill */}
                <div
                  className="absolute top-0 left-0 h-full rounded-[50px] transition-all duration-300 ease-out"
                  style={{
                    width: `${percentage}%`,
                    backgroundColor: config.color,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  }
);

TransmissionDistributionChart.displayName = 'TransmissionDistributionChart';

export { TransmissionDistributionChart };
